import React, { useState } from 'react';
import FoilTable from '../components/FoilTable';
import { M3NRow, M3N_COLUMNS, M3N_FILTERS, M3N_SEARCH, M3N_VALIDATE, M3NSearchContext, M3N_PREPARE_SEARCH } from '../configs/m3n.config';
import { resolveFoilAvailability, resolveRowDetails, indexRows, filterSelectedRows } from '../util/rows';
import Filter from '../components/Filter';
import styles from './Modal.module.css';
import headerStyles from '../components/Header.module.css';
import { useProductModel } from '../util/useProductModel';
import { filterRows } from '../util/filter';
import Modal from './Modal';
import { fetchBatches, excludeCurrentBatch } from '../util/batch';
import HeaderField from '../components/HeaderField';
import HeaderButtons from '../components/HeaderButtons';
import Footer from '../components/Footer';
import { useReloadable } from '../util/useReloadable';
import ErrorMessage from '../components/ErrorMessage';
import { resolvePastedFoilNumbers } from '../util/clipboard';


const M3N = ({ createMode }: { createMode: boolean }) => {
    const [rows, setRows] = useState<M3NRow[]>([]);
    const [indexedRows, setIndexedRows] = useState<{ [foilNumber: string]: M3NRow }>();
    const [query, setQuery] = useState<string>('');
    const [activeFilters, setActiveFilters] = useState<number[]>([]);

    const [product, updateProduct, productRef, initialProductRef] = useProductModel();

    const { loading, reload, error } = useReloadable(async () => {
        const [inputBatches, existingCBatches, existingLBatches] = await fetchBatches(['^N-', '^C-', '^L-']);
        const existingOutputBatches = existingCBatches.concat(existingLBatches);
        const outputBatches = excludeCurrentBatch(existingOutputBatches, productRef.current?.identifier);

        const rows: M3NRow[] = resolveFoilAvailability<M3NRow>(inputBatches, outputBatches, 'foils');
        const availableRows = rows.filter((row) => row.available);
        const detailedRows: M3NRow[] = await resolveRowDetails(availableRows, M3N_COLUMNS, inputBatches);

        setRows(detailedRows);
        setIndexedRows(indexRows(detailedRows));
    }, []);

    const batchNumber = product?.identifier || '';
    const visibleRows = filterRows<M3NRow, M3NSearchContext>(rows, M3N_FILTERS, M3N_SEARCH, activeFilters, M3N_PREPARE_SEARCH(query));
    const selectedRowKeys = product?.parameters?.foils || [];
    const selectedRows = filterSelectedRows(selectedRowKeys, indexedRows);
    const hasSelectedRows = selectedRows.length > 0;

    const updateSelectedRowKeys = (selectedRowKeys: string[]) => {
        updateProduct({
            parameters: {
                foils: selectedRowKeys,
            }
        });
    }

    const updateBatchNumber = (newBatchNumber: string) => {
        updateProduct({
            identifier: newBatchNumber
        });
    }

    const handlePasteFoils = async (foilNumbers: string[]) => {
        const newSelectedRowKeys = await resolvePastedFoilNumbers(foilNumbers, selectedRowKeys, [], indexedRows);

        if (!newSelectedRowKeys) {
            return;
        }

        updateSelectedRowKeys(newSelectedRowKeys);
    }
    if (error) {
        return (
            <ErrorMessage error={error} />
        )
    }

    return (
        <Modal
            loading={loading}
            onBeforeSubmit={() => M3N_VALIDATE(product, createMode, initialProductRef.current)}
        >
            <header className={headerStyles.topHeader}>
                <HeaderField
                    id={'batchNumber'}
                    label={'Batch number'}
                    value={batchNumber}
                    onChange={updateBatchNumber}
                />
            </header>
            <header className={headerStyles.bottomHeader}>
                <Filter
                    query={query}
                    setQuery={setQuery}
                    filters={M3N_FILTERS}
                    activeFilters={activeFilters}
                    setActiveFilters={setActiveFilters}
                />
                <HeaderButtons<M3NRow>
                    columns={M3N_COLUMNS}
                    rows={rows}
                    exportFilename={'Azumuta_export_M3N'}
                    onPasteFoils={handlePasteFoils}
                    onReload={reload}
                />
            </header>
            <section className={styles.inputTableWrapper}>
                <FoilTable<M3NRow>
                    columns={M3N_COLUMNS}
                    rows={visibleRows}
                    selectedRowKeys={selectedRowKeys}
                    sortable={true}
                    onRowToggle={updateSelectedRowKeys}
                />
            </section>
            {hasSelectedRows &&
                <section className={styles.outputTableWrapper}>
                    <FoilTable<M3NRow>
                        columns={M3N_COLUMNS}
                        rows={selectedRows}
                        selectedRowKeys={selectedRowKeys}
                        onRowToggle={updateSelectedRowKeys}
                    />
                </section>
            }
            {hasSelectedRows &&
                <Footer
                    selectedRowCount={selectedRowKeys.length}
                    onClear={() => updateSelectedRowKeys([])}
                />
            }
        </Modal>
    );
}

export default M3N;

